/**
    * @description      : 
    * @author           : USER
    * @group            : 
    * @created          : 15/11/2025 - 14:30:16
    * 
    * MODIFICATION LOG
    * - Version         : 1.0.0
    * - Date            : 15/11/2025
    * - Author          : USER
    * - Modification    : 
**/
import React, { useEffect, useLayoutEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ClerkProvider, SignedIn, SignIn, SignUp, UserProfile, useAuth } from '@clerk/clerk-react';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { getClerkLocale } from './utils/clerkLocales';
import Sidebar from './components/Sidebar/Sidebar';
import Chat from './components/Chat/Chat';
import AdminPage from './components/Admin/AdminPage';
import AdminRoute from './components/Auth/AdminRoute';
import Settings from './components/Settings/Settings';
import { clearClerkTokenProvider, setClerkTokenProvider } from './services/clerkToken';
import { initScrollFogEffect } from './utils/scrollFogEffect';
import './components/Auth/Auth.css';
import './styles/globals.css';

// Suppress all console output in production
if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.warn = () => {};
  console.debug = () => {};
  console.info = () => {};
}

const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

const clerkDarkVariables = {
  colorPrimary: '#1a73e8',
  colorBackground: '#ffffff',
  colorInputBackground: '#f8fafd',
  colorInputText: '#1f1f1f',
  colorText: '#1f1f1f',
  colorTextSecondary: '#5f6368',
  colorTextOnPrimaryBackground: '#ffffff',
  borderRadius: '1rem',
};

const clerkDarkCard = {
  boxShadow: '0 24px 64px rgba(60, 64, 67, 0.18)',
  borderRadius: '1.5rem',
  border: '1px solid #dfe5ee',
  background: 'rgba(255, 255, 255, 0.98)',
  backdropFilter: 'blur(18px)',
};

const clerkDarkField = {
  borderRadius: '1rem',
  border: '1px solid #dfe5ee',
  background: '#f8fafd',
  color: '#1f1f1f',
};

const clerkAuthDarkAppearance = {
  variables: clerkDarkVariables,
  elements: {
    rootBox: { width: '100%' },
    card: clerkDarkCard,
    headerTitle: { fontSize: '1.5rem', fontWeight: '700', color: '#1f1f1f' },
    headerSubtitle: { color: '#5f6368' },
    socialButtonsBlockButton: {
      borderRadius: '9999px',
      border: '1px solid #dfe5ee',
      fontWeight: '500',
      color: '#1f1f1f',
      background: '#ffffff',
    },
    socialButtonsBlockButtonText: { color: '#1f1f1f' },
    dividerLine: { background: '#dfe5ee' },
    dividerText: { color: '#5f6368' },
    formFieldLabel: { color: '#1f1f1f', fontWeight: '600' },
    formFieldInput: clerkDarkField,
    formButtonPrimary: {
      borderRadius: '9999px',
      fontWeight: '600',
      fontSize: '0.9375rem',
      background: '#1a73e8',
      color: '#ffffff',
    },
    footer: { justifyContent: 'flex-start' },
    footerAction: { justifyContent: 'flex-start' },
    footerActionLink: { color: '#1a73e8', fontWeight: '600' },
    footerPages: { justifyContent: 'flex-start' },
    footerPagesLink: { justifyContent: 'flex-start' },
    identityPreviewEditButton: { color: '#1a73e8' },
  },
};

const clerkProfileDarkAppearance = {
  variables: {
    ...clerkDarkVariables,
    fontSize: '15px',
    fontWeight: { normal: 500, medium: 600, bold: 700 },
  },
  elements: {
    card: {
      ...clerkDarkCard,
      borderRadius: '24px',
      maxWidth: '900px',
      width: '100%',
    },
    modalBackdrop: { background: 'rgba(32, 33, 36, 0.28)' },
    modalContent: { background: '#ffffff' },
    navbar: {
      background: '#f8fafd',
      borderRightColor: '#dfe5ee',
    },
    navbarButton: { color: '#1f1f1f' },
    navbarButtonActive: {
      color: '#1a73e8',
      fontWeight: '700',
      background: '#e8f0fe',
    },
    pageScrollBox: {
      padding: '2rem',
      background: '#ffffff',
    },
    page: { background: '#ffffff' },
    profileSection: { borderBottomColor: '#dfe5ee' },
    profileSectionTitle: {
      color: '#1f1f1f',
      borderBottomColor: '#dfe5ee',
    },
    profileSectionTitleText: { color: '#1f1f1f' },
    profileSectionContent: { color: '#1f1f1f' },
    profileSectionPrimaryButton: {
      color: '#1f1f1f',
      borderColor: '#dfe5ee',
      background: '#ffffff',
    },
    userPreviewMainIdentifier: { color: '#1f1f1f' },
    userPreviewSecondaryIdentifier: { color: '#5f6368' },
    formButtonPrimary: {
      backgroundColor: '#1a73e8',
      color: '#ffffff',
      fontWeight: '700',
    },
    formButtonReset: { color: '#1f1f1f' },
    formFieldLabel: { color: '#1f1f1f', fontWeight: '600' },
    formFieldInput: clerkDarkField,
    headerTitle: { color: '#1f1f1f' },
    headerSubtitle: { color: '#5f6368' },
    menuItem: { color: '#1f1f1f' },
    menuItemButton: { color: '#1f1f1f' },
    breadcrumbs: { color: '#5f6368' },
    breadcrumbsItem: { color: '#5f6368' },
    breadcrumbsItemDivider: { color: '#5f6368' },
    badge: {
      color: '#1a73e8',
      background: '#e8f0fe',
    },
    footer: { justifyContent: 'flex-start' },
  },
};

function ClerkTokenBridge() {
  const { getToken, isLoaded, isSignedIn } = useAuth();

  useLayoutEffect(() => {
    if (!isLoaded) {
      clearClerkTokenProvider();
      return undefined;
    }

    setClerkTokenProvider((options) => getToken(options), { isLoaded, isSignedIn });

    return () => {
      clearClerkTokenProvider();
    };
  }, [getToken, isLoaded, isSignedIn]);

  return null;
}

// Main App Component with Clerk Provider
function AppContent() {
  const { languageCode } = useLanguage();
  const { isSignedIn } = useAuth();
  // Force light theme only - dark mode removed
  const theme = 'light';
  const toggleTheme = () => {}; // No-op - kept for backwards compatibility

  useEffect(() => {
    // Always apply light theme
    document.documentElement.setAttribute('data-theme', 'light');
    localStorage.setItem('tuliza-theme', 'light');
  }, []);

  useEffect(() => {
    // Initialize scroll-based fog effect
    const cleanup = initScrollFogEffect();
    
    return () => {
      if (cleanup) cleanup();
    };
  }, []);

  if (!clerkPubKey) {
    return <div style={{padding: '2rem', color: 'red'}}>Missing REACT_APP_CLERK_PUBLISHABLE_KEY in .env</div>;
  }

  const clerkLocale = getClerkLocale(languageCode);

  return (
    <Router>
      <Routes>
            {/* Default to Chat - Available to everyone (anonymous + auth) */}
            <Route path="/" element={
              <div className={`app-container ${isSignedIn ? 'with-sidebar' : 'without-sidebar'}`}>
                {/* Show sidebar only when signed in */}
                <SignedIn>
                  <Sidebar theme={theme} toggleTheme={toggleTheme} />
                </SignedIn>
                <main className="main-content">
                  <Chat />
                </main>
              </div>
            } />

            {/* Chat alias route */}
            <Route path="/chat" element={<Navigate to="/" replace />} />

          {/* Clerk Auth Pages */}
          <Route
            path="/sign-in/*"
            element={
              <div className="auth-page">
                <div className="auth-card-wrapper">
                  <SignIn
                    routing="path"
                    path="/sign-in"
                    signUpUrl="/sign-up"
                    afterSignInUrl="/"
                    appearance={clerkAuthDarkAppearance}
                  />
                </div>
              </div>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <div className="auth-page">
                <div className="auth-card-wrapper auth-card-wrapper--wide">
                  <SignUp
                    routing="path"
                    path="/sign-up"
                    signInUrl="/sign-in"
                    afterSignUpUrl="/"
                    appearance={clerkAuthDarkAppearance}
                  />
                </div>
              </div>
            }
          />

          {/* Protected Routes - Require authentication */}
          <Route path="/profile" element={
            <SignedIn>
              <div className="app-container">
                <Sidebar theme={theme} toggleTheme={toggleTheme} />
                <main className="main-content" style={{ overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '2rem', background: '#f8fafd' }}>
                  <UserProfile 
                    appearance={clerkProfileDarkAppearance}
                  />
                </main>
              </div>
            </SignedIn>
          } />
          <Route path="/settings" element={
            <SignedIn>
              <div className="app-container">
                <Sidebar theme={theme} toggleTheme={toggleTheme} />
                <main className="main-content">
                  <Settings />
                </main>
              </div>
            </SignedIn>
          } />
          {/* Upgrade Plan - Commented out for future use
          <Route path="/upgrade" element={
            <SignedIn>
              <div className="app-container">
                <Sidebar theme={theme} toggleTheme={toggleTheme} />
                <main className="main-content">
                  <Upgrade />
                </main>
              </div>
            </SignedIn>
          } />
          */}
          <Route path="/learn-more" element={<Navigate to="/" replace />} />

          {/* Admin-Only Routes - Standalone without sidebar */}
          <Route path="/admin" element={
            <SignedIn>
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            </SignedIn>
          } />
          <Route path="/admin/*" element={
            <SignedIn>
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            </SignedIn>
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
  );
}

// Wrapper to provide Clerk with the proper locale
function App() {
  return (
    <ClerkProvider publishableKey={clerkPubKey}>
      <ClerkTokenBridge />
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </ClerkProvider>
  );
}

export default App;
